import Folderable from "./mixins/folderable";
import Form from "./form";
import License from "./license";
import Capable from "../../../common/mixins/capable";
import Organization from "../../../common/models/organization";
import {RelationType} from "../../../common/models/relationType";
import Certificate from "./certificate";
import Shareable from "../../../common/mixins/shareable";
import Share from "../../../common/models/share";
import ObjectModel from "./objectModel";
import {ArrayHelper} from "../../../common/helpers/arrayHelper";
import ListView from "../../../common/models/listViews/listView";
import DetailView from "../../../common/models/detailView/detailView";
import {
  FolderDocumentFindKeyMap,
  TOrganizationDocument
} from "../../../common/interfaces/organizationDocumentInterfaces";
import Mapping from "../../../common/models/mapping/mapping";
import PublicProfile from "../../../common/models/publicProfile/publicProfile";
import ProcessFlow from "~/models/processFlows/processFlow";
import Application from "./application";

export default class Folder extends Shareable(Capable(Folderable)) {
  id: string;
  name: string;
  folders: Folder[] = [];
  parent: Organization | Folder | Application;
  c_at: string = null;
  u_at: string = null;
  expanded: boolean = false;
  moving: boolean = false;
  except: string[] = ['expanded', 'moving', 'fetchingDocuments', 'shares'];
  organization_id: string;

  static get displayName(): string {
    return 'Folder';
  }

  get collectionKey(): string {
    return 'folders';
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    return {
      'folders': { type: Folder, relationType: RelationType.array },
      'forms': { type: Form, relationType: RelationType.array },
      'licenses': { type: License, relationType: RelationType.array },
      'shares': {type: Share, relationType: RelationType.collection},
      'certificates': { type: Certificate, relationType: RelationType.array },
      'object_models': { type: ObjectModel, relationType: RelationType.array },
      'list_views': { type: ListView, relationType: RelationType.array },
      'detail_views': { type: DetailView, relationType: RelationType.array },
      'mappings': {type: Mapping, relationType: RelationType.array},
      'public_profiles': {type: PublicProfile, relationType: RelationType.array},
      'process_flows': {type: ProcessFlow, relationType: RelationType.array},
    };
  }

  createEmbeddedObject = {
    list_views: (parent, json): ListView => {
      const doc = new ListView();
      doc._track = false;
      return doc;
    },
    detail_views: (parent, json): DetailView => {
      const doc = new DetailView();
      doc._track = false;
      return doc;
    },
    mappings: (parent, json): Mapping => {
      const doc = new Mapping();
      doc._track = false;
      return doc;
    },
    public_profiles: (parent, json): PublicProfile => {
      const doc = new PublicProfile();
      doc._track = false;
      return doc;
    },
    process_flows: (parent, json): ProcessFlow => {
      const doc = new ProcessFlow();
      doc._track = false;
      return doc;
    },
  };

  get organization() {
    return Organization.repo.find(this.organization_id);
  }

  set organization(organization) {
    this.organization_id = organization.id;
  }

  get parentIsFolder(): boolean {
    return this.parent instanceof Folder;
  }

  get displayFolderName(): string {
    if (this.isArchiveFolder() && this.name === 'Trash') {
      return 'Archive';
    }
    return this.name;
  }

  isRootFolder(): boolean {
    return !(this.parent instanceof Folder);
  }

  isArchiveFolder(): boolean {
    return (this.name === 'Archive' || this.name === 'Trash') && this.isRootFolder();
  }

  hasForms(): boolean {
    return this.forms && this.forms.length > 0;
  }

  hasLicenses(): boolean {
    return this.licenses && this.licenses.length > 0;
  }

  hasCertificates(): boolean {
    return this.certificates && this.certificates.length > 0;
  }

  hasObjectModels(): boolean {
    return this.object_models && this.object_models.length > 0;
  }

  hasListViews(): boolean {
    return this.list_views && this.list_views.length > 0;
  }

  hasDetailViews(): boolean {
    return this.detail_views && this.detail_views.length > 0;
  }

  hasMappings(): boolean {
    return this.mappings && this.mappings.length > 0;
  }

  hasPublicProfiles(): boolean {
    return this.public_profiles && this.public_profiles.length > 0
  }

  hasProcessFlows(): boolean {
    return this.process_flows && this.process_flows.length > 0
  }

  get ancestors(): Folder[] {
    const folders = [];
    this.eachAncestor((ancestor) => folders.push(ancestor));
    return folders;
  }

  eachAncestor(callback) {
    if (!(this.parent instanceof Folder)) return;
    callback(this.parent);
    this.parent.eachAncestor(callback);
  }

  isAnyInHierarchy(func) {
    if (func(this)) {
      return true;
    }
    if (!(this.parent instanceof Folder)) return false;
    return this.parent.isAnyInHierarchy(func);
  }

  isAnyInDescendants(func) {
    if (func(this)) {
      return true;
    }
    if (this.folders.length === 0) return false;
    return this.folders.some(folder => folder.isAnyInDescendants(func));
  }

  idTree(): string[] {
    const tree = [this.id];

    this.eachAncestor((ancestor) => {
      tree.push(ancestor.id);
    });
    return tree;
  }

  open(): void {
    this.openAncestors();
    this.expanded = true;
  }

  openAncestors(): void {
    this.eachAncestor((ancestor) => ancestor.open());
  }

  addDocument(doc: TOrganizationDocument): TOrganizationDocument {
    const collection = this[doc.collectionKey];
    const index = collection.indexOf(doc);
    if (index === -1) {
      collection.push(doc);
    }
    doc.parent = this;
    return doc;
  }

  removeDocument(doc: TOrganizationDocument):TOrganizationDocument {
    const collection = this[doc.collectionKey];
    const index = collection.indexOf(doc);
    if (index > -1) {
      collection.splice(index, 1);
      doc.parent = null;
      return doc;
    } else {
      return null;
    }
  }

  hasForm(id: string): boolean {
    return this.forms.some(form => form.id === id);
  }

  canHaveContextMenu(): boolean {
    return !this.isArchiveFolder() || !this.isRootFolder();
  }

  get organizationDocuments(): (TOrganizationDocument)[] {
    return this.organizationDocumentsBy('title');
  }

  get allChildDocuments(): (TOrganizationDocument)[] {
    return Object.keys(FolderDocumentFindKeyMap).reduce((agg, k) => {
      return agg.concat(this[k])
    }, [])
  }

  organizationDocumentsBy(sortBy: string = 'title'): TOrganizationDocument[] {
    return ArrayHelper.sortByString(this.allChildDocuments, sortBy);
  }

  hasLicense(id: string): boolean {
    return this.licenses.some(license => license.id === id);
  }

  hasCertificate(id: string): boolean {
    return this.certificates.some(cert => cert.id === id);
  }

  isDescendantOf(folder: Folder): boolean {
    return folder.descendants.includes(this);
  }

  canMoveTo(folder: Folder): boolean {
    return this !== folder && !folder.isDescendantOf(this);
  }

  get rootName(): string {
      return "folders"
  }

}
